import React, { ReactElement, useState } from 'react';
import { Container } from './Container';
import { LanguageSwitcher } from '../i18n/components/LanguageSwitcher';
import { Logo } from './Logo';
import styled from 'styled-components';
import { Colors } from '../constants/colors';
import { Sizes } from '../constants/sizes';
import { AnchoredLink } from './AnchoredLink';
import { Device } from '../constants/device';
import menuIcon from '../../assets/images/menu.svg';
import closeIcon from '../../assets/images/close.svg';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { StyledMenuLink, StyledMenuRouterLink } from './StyledMenuLink';
import { localizedPath } from '../helpers/localizedPath';
import { AI_SERVICES_PATH, CONSULTING_PATH } from '../constants/routes';
import { scrollToTop } from '../helpers/scrollToTop';
import { showsNclp } from '../helpers/showsNclp';
import { ServiceLink, ServicesMenu } from './ServicesMenu';

const NCLP_CATALOGUE = 'https://nclp.cz';

export const Header = (): ReactElement | null => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const sectionLinks = ['features', 'references', 'teams', 'contact'];
  // The NČLP catalogue is Czech only (see helpers/showsNclp).
  const serviceLinks: ServiceLink[] = [
    { to: localizedPath(pathname, CONSULTING_PATH), label: t('consulting.nav') },
    { to: localizedPath(pathname, AI_SERVICES_PATH), label: t('aiServices.nav') },
    ...(showsNclp(i18n.language) ? [{ to: NCLP_CATALOGUE, label: t('navigation.nclpCatalogue'), external: true }] : []),
  ];

  return (
    <FixedHeader>
      <Container>
        <HeaderContent>
          <Logo />
          <HeaderContentMiddle className={'styled-navigation'}>
            <NavLinks>
              {sectionLinks.map((section, key) => (
                <AnchoredLink key={key} anchor={section} color={Colors.primary}>
                  {t(`section.${section}`)}
                </AnchoredLink>
              ))}
              {/* A dropdown with a single entry would only hide the link, so it stays plain until there is a second one. */}
              {serviceLinks.length > 1 ? (
                <ServicesMenu label={t('navigation.moreServices')} links={serviceLinks} />
              ) : (
                <StyledMenuRouterLink to={serviceLinks[0].to} onClick={scrollToTop} color={Colors.primary}>
                  {serviceLinks[0].label}
                </StyledMenuRouterLink>
              )}
            </NavLinks>
          </HeaderContentMiddle>
          <HeaderContentEnd>
            <HeaderDivider />
            <LanguageSwitcher />
          </HeaderContentEnd>
          <Burger onClick={() => setShowMobileMenu((show) => !show)}>
            <img src={menuIcon} alt="Menu icon" />
          </Burger>

          {showMobileMenu && (
            <MobileMenu>
              <MobileHeaderContent className={'mobileHeaderContent'}>
                <Logo />
                <Burger onClick={() => setShowMobileMenu((show) => !show)}>
                  <img src={closeIcon} alt="Close icon" />
                </Burger>
              </MobileHeaderContent>
              <NavContainer>
                <nav>
                  {sectionLinks.map((section, key) => (
                    <AnchoredLink key={key} anchor={section} onClick={() => setShowMobileMenu(false)} color={Colors.primary}>
                      {t(`section.${section}`)}
                    </AnchoredLink>
                  ))}
                  {serviceLinks.length > 1 && <MobileGroupLabel>{t('navigation.moreServices')}</MobileGroupLabel>}
                  {serviceLinks.map((link) =>
                    link.external ? (
                      <StyledMenuLink key={link.to} href={link.to} target="_blank" rel="noopener noreferrer" onClick={() => setShowMobileMenu(false)} color={Colors.primary}>
                        {link.label} ↗
                      </StyledMenuLink>
                    ) : (
                      <StyledMenuRouterLink
                        key={link.to}
                        to={link.to}
                        onClick={() => {
                          setShowMobileMenu(false);
                          scrollToTop();
                        }}
                        color={Colors.primary}
                      >
                        {link.label}
                      </StyledMenuRouterLink>
                    ),
                  )}
                </nav>
                <LanguageSwitcher />
              </NavContainer>
            </MobileMenu>
          )}
        </HeaderContent>
      </Container>
    </FixedHeader>
  );
};

const FixedHeader = styled.header`
  width: 100%;
  z-index: 10;
  background: ${Colors.white};
`;

const Burger = styled.button`
  width: 30px;
  height: 30px;
  padding: 0;
  background: none;
  border: none;

  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;

  ${Device.mobile} {
    height: 20px;
    width: 20px;
  }

  img {
    width: 100%;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  height: ${Sizes.HeaderHeight}px;

  ${Device.laptop} {
    align-items: center;
  }

  ${Burger} {
    display: none;

    ${Device.laptop} {
      display: block;
    }
  }
`;

const HeaderContentMiddle = styled.nav`
  display: flex;
  flex: 1;

  ${Device.laptop} {
    display: none;
  }
`;

const HeaderContentEnd = styled.div`
  display: flex;
  align-items: center;

  ${Device.laptop} {
    display: none;
  }
`;

const NavLinks = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 950px;
  margin-left: 140px;

  a {
    white-space: nowrap;
  }

  ${Device.fullHd} {
    margin-left: 60px;
  }

  ${Device.desktop} {
    width: 100%;
    justify-content: end;
    gap: 30px;
    margin-right: 20px;
  }
`;

const HeaderDivider = styled.div`
  width: 2px;
  height: 25px;
  background: ${Colors.primary};
  margin-right: 20px;
  padding-top: 15px;
  display: none;

  ${Device.desktop} {
    display: flex;
  }

  ${Device.laptop} {
    display: none;
  }
`;

const MobileHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: ${Sizes.HeaderHeight}px;
  padding: 0 30px;
  width: 100%;
`;

const NavContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  height: 100%;
  padding-bottom: ${Sizes.HeaderHeight}px;
`;

const MobileGroupLabel = styled.span`
  margin: 20px 0 0;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${Colors.darkGrey};
`;

const MobileMenu = styled.div`
  position: fixed;
  z-index: 100;
  background: ${Colors.white};
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;

  display: flex;
  flex-direction: column;
  align-items: center;

  nav {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 30px 0;
  }

  a {
    color: ${Colors.primary};
    margin: 10px 0;
  }
`;
